import { createFileRoute, Outlet, Link, useRouterState } from "@tanstack/react-router";
import { useScope } from "@/hooks/use-scope";
import {
  useImplementationProgress,
  TRACK_META,
  type TrackId,
} from "@/lib/implementation-progress";
import { CheckCircle2, Circle, Loader2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/implementation/workspace")({
  component: WorkspaceLayout,
  head: () => ({
    meta: [
      { title: "Implementation Workspace — CairnOS" },
      { name: "description", content: "The command center for standing up your warehouse." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

const TABS: { id: TrackId | "overview"; label: string; to: string; exact?: boolean }[] = [
  { id: "overview", label: "Overview", to: "/app/implementation/workspace", exact: true },
  { id: "foundation", label: "Foundation", to: "/app/implementation/workspace/foundation" },
  { id: "data", label: "Data", to: "/app/implementation/workspace/data" },
  { id: "operations", label: "Operations", to: "/app/implementation/workspace/operations" },
  { id: "validation", label: "Validation", to: "/app/implementation/workspace/validation" },
];

function WorkspaceLayout() {
  const { tenantId, tenants } = useScope();
  const tenantName = tenants.find((t) => t.id === tenantId)?.name ?? "Your warehouse";
  const progress = useImplementationProgress(tenantId);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  return (
    <div className="min-h-[calc(100vh-var(--app-shell-header,3.5rem))] bg-background">
      {/* Workspace header */}
      <div className="border-b border-border bg-card">
        <div className="mx-auto max-w-6xl px-6 pt-8 pb-4">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary" />
            Implementation Workspace
          </div>
          <div className="mt-3 flex flex-wrap items-end justify-between gap-6">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
                {tenantName}
              </h1>
              <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-border bg-background/60 px-3 py-1 text-[11px] font-medium text-muted-foreground">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary" />
                <span className="uppercase tracking-[0.14em] text-muted-foreground/80">
                  Auditing evidence for
                </span>
                <span className="font-semibold text-foreground">{tenantName}</span>
              </div>
              <p className="mt-3 text-sm text-muted-foreground">
                Four tracks running in parallel. One shared definition of done.
              </p>
            </div>
            <OverallMeter percent={progress.overallPercent} loading={progress.loading} />
          </div>

          {/* Track tabs */}
          <nav className="mt-6 -mb-px flex flex-wrap gap-x-1 gap-y-2 border-b border-border">
            {TABS.map((tab) => {
              const active = tab.exact
                ? pathname === tab.to
                : pathname === tab.to || pathname.startsWith(tab.to + "/");
              const trackProgress =
                tab.id !== "overview" ? progress.tracks[tab.id as TrackId] : null;
              return (
                <Link
                  key={tab.id}
                  to={tab.to}
                  className={
                    "group inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors " +
                    (active
                      ? "border-primary text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground")
                  }
                >
                  {trackProgress ? (
                    <StatusDot status={trackProgress.status} />
                  ) : null}
                  {tab.label}
                  {trackProgress ? (
                    <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] tabular-nums text-muted-foreground">
                      {trackProgress.completed}/{trackProgress.total}
                    </span>
                  ) : null}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-6 py-8">
        <Outlet />
      </div>
    </div>
  );
}

function OverallMeter({ percent, loading }: { percent: number; loading: boolean }) {
  return (
    <div className="min-w-[220px]">
      <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        <span>Implementation progress</span>
        <span className="tabular-nums text-foreground">
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : `${percent}%`}
        </span>
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full bg-primary transition-[width] duration-500"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

export function StatusDot({ status }: { status: "not_started" | "in_progress" | "complete" }) {
  if (status === "complete") {
    return <CheckCircle2 className="h-3.5 w-3.5 text-[color:var(--success)]" />;
  }
  if (status === "in_progress") {
    return <span className="inline-block h-2 w-2 rounded-full bg-primary" />;
  }
  return <Circle className="h-3.5 w-3.5 text-muted-foreground/60" />;
}

export { TRACK_META };